import { Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@agion/ui'
import { useTranslation } from 'react-i18next'
import type { LaunchInput } from '../../types/mission'
import type { LaunchInputsForm } from '../../hooks/useLaunchInputs'

/** One explicit field-to-upload association, shared by both chat surfaces. */
export function LaunchFileInput({ input, form, id, disabled }: {
  input: LaunchInput
  form: LaunchInputsForm
  id: string
  disabled?: boolean
}) {
  const { t } = useTranslation('chat')
  const blocked = disabled || form.canStart !== true
  const selected = form.files?.find((file) => file.id === form.values[input.name])
  return (
    <div className="flex flex-col gap-2">
      <Select name={input.name} value={form.values[input.name] ?? ''}
        onValueChange={(value) => { if (!blocked) form.setValue(input.name, value) }} disabled={blocked}>
        <SelectTrigger id={id} aria-required={input.required} aria-invalid={!!form.marked[input.name]}>
          <SelectValue placeholder={t('launchInputs.selectFile', 'Select an uploaded file')} />
        </SelectTrigger>
        <SelectContent>
          {form.files?.map((file) => <SelectItem key={file.id} value={file.id}>{file.filename}</SelectItem>)}
        </SelectContent>
      </Select>
      {form.canStart === true && (
        <>
          <Label htmlFor={`${id}-upload`}>{t('launchInputs.uploadFile', 'Upload a file')}</Label>
          <Input id={`${id}-upload`} type="file" disabled={blocked}
            onChange={(event) => {
              if (blocked) return
              const file = event.target.files?.[0]
              if (file) form.uploadFile?.(input.name, file)
              event.target.value = ''
            }} />
        </>
      )}
      {selected?.status === 'uploading' && <p role="status" className="text-sm">{t('launchInputs.uploadingFile', 'Uploading file…')}</p>}
      {selected?.status === 'error' && (
        <div className="text-sm">
          <p role="alert">{selected.error ?? t('launchInputs.uploadFailed', 'File upload failed.')}</p>
          {form.canStart === true && <Button type="button" variant="ghost" disabled={blocked} onClick={() => { if (!blocked) form.retryFile?.(selected.id) }}>{t('launchInputs.retryUpload', 'Retry upload')}</Button>}
        </div>
      )}
      {selected && <Button type="button" variant="ghost" disabled={disabled} onClick={() => {
        form.removeFile?.(selected.id)
        form.setValue(input.name, '')
      }}>{t('launchInputs.removeFile', 'Remove file')}</Button>}
    </div>
  )
}
